/**
 * AnnotationModal.jsx — Phase 148 (Studio Creative Hub). Rich image markup
 * tool: Pen, Arrow, Text, Box tools, each committed annotation is its own
 * "layer" (listed + individually deletable on the right), plus a full
 * Undo/Redo history. "Save & Share" flattens everything onto the source
 * image, uploads the result, and hands the new attachment back to the
 * caller (Chat.jsx posts it as a new reply in the same thread).
 *
 * Precision over prose: instead of typing "move the logo left", draw an
 * arrow straight on the design.
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, MoveUpRight, Type, Square, Undo2, Redo2, Trash2, X, Loader2, Layers } from "lucide-react";

const COLORS = ["#F54800", "#EF4444", "#22C55E", "#3B82F6", "#EAB308", "#111827"];
const TOOLS = [
  { id: "pen", label: "Pen", icon: Pencil },
  { id: "arrow", label: "Arrow", icon: MoveUpRight },
  { id: "text", label: "Text", icon: Type },
  { id: "box", label: "Box", icon: Square },
];
const MAX_W = 760;

function layerLabel(obj, idx) {
  if (obj.type === "text") return `Text: "${(obj.text || "").slice(0, 18)}"`;
  const names = { pen: "Pen stroke", arrow: "Arrow", box: "Box" };
  return `${names[obj.type] || obj.type} #${idx + 1}`;
}

function drawObject(ctx, obj) {
  ctx.strokeStyle = obj.color;
  ctx.fillStyle = obj.color;
  ctx.lineWidth = 3;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  if (obj.type === "pen") {
    if (obj.points.length < 2) return;
    ctx.beginPath();
    ctx.moveTo(obj.points[0].x, obj.points[0].y);
    obj.points.slice(1).forEach((p) => ctx.lineTo(p.x, p.y));
    ctx.stroke();
  } else if (obj.type === "box") {
    ctx.strokeRect(obj.x, obj.y, obj.w, obj.h);
  } else if (obj.type === "arrow") {
    const { x1, y1, x2, y2 } = obj;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
    const angle = Math.atan2(y2 - y1, x2 - x1);
    const head = 14;
    ctx.beginPath();
    ctx.moveTo(x2, y2);
    ctx.lineTo(x2 - head * Math.cos(angle - Math.PI / 6), y2 - head * Math.sin(angle - Math.PI / 6));
    ctx.lineTo(x2 - head * Math.cos(angle + Math.PI / 6), y2 - head * Math.sin(angle + Math.PI / 6));
    ctx.closePath();
    ctx.fill();
  } else if (obj.type === "text") {
    ctx.font = "600 20px 'Cabinet Grotesk', sans-serif";
    ctx.fillText(obj.text, obj.x, obj.y);
  }
}

export default function AnnotationModal({ open, onClose, imageUrl, imageName, onSaved }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const [dims, setDims] = useState({ w: MAX_W, h: MAX_W });
  const [tool, setTool] = useState("pen");
  const [color, setColor] = useState(COLORS[0]);
  const [objects, setObjects] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const drawingRef = useRef(null);
  const [, forceTick] = useState(0); // re-render while drawingRef mutates mid-stroke
  const [textPrompt, setTextPrompt] = useState(null); // {x,y}
  const [saving, setSaving] = useState(false);

  // Reset state + (re)load the source image whenever a new image is opened.
  useEffect(() => {
    if (!open || !imageUrl) return;
    setObjects([]); setRedoStack([]); setTool("pen"); setTextPrompt(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const scale = Math.min(1, MAX_W / img.naturalWidth);
      setDims({ w: Math.round(img.naturalWidth * scale), h: Math.round(img.naturalHeight * scale) });
      imgRef.current = img;
      forceTick((n) => n + 1);
    };
    img.src = imageUrl;
  }, [open, imageUrl]);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imgRef.current) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(imgRef.current, 0, 0, canvas.width, canvas.height);
    objects.forEach((o) => drawObject(ctx, o));
    if (drawingRef.current) drawObject(ctx, drawingRef.current);
  }, [objects]);

  useEffect(() => { redraw(); }, [redraw, dims]);

  const posFromEvent = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onMouseDown = (e) => {
    const p = posFromEvent(e);
    if (tool === "text") { setTextPrompt(p); return; }
    if (tool === "pen") drawingRef.current = { type: "pen", color, points: [p] };
    else if (tool === "arrow") drawingRef.current = { type: "arrow", color, x1: p.x, y1: p.y, x2: p.x, y2: p.y };
    else if (tool === "box") drawingRef.current = { type: "box", color, x: p.x, y: p.y, w: 0, h: 0 };
    forceTick((n) => n + 1);
  };
  const onMouseMove = (e) => {
    if (!drawingRef.current) return;
    const p = posFromEvent(e);
    const d = drawingRef.current;
    if (d.type === "pen") d.points.push(p);
    else if (d.type === "arrow") { d.x2 = p.x; d.y2 = p.y; }
    else if (d.type === "box") { d.w = p.x - d.x; d.h = p.y - d.y; }
    redraw();
  };
  const onMouseUp = () => {
    if (!drawingRef.current) return;
    setObjects((prev) => [...prev, drawingRef.current]);
    setRedoStack([]);
    drawingRef.current = null;
  };

  const commitText = (value) => {
    if (value.trim()) {
      setObjects((prev) => [...prev, { type: "text", color, x: textPrompt.x, y: textPrompt.y, text: value.trim() }]);
      setRedoStack([]);
    }
    setTextPrompt(null);
  };

  const undo = () => {
    if (objects.length === 0) return;
    setRedoStack((r) => [objects[objects.length - 1], ...r]);
    setObjects((prev) => prev.slice(0, -1));
  };
  const redo = () => {
    if (redoStack.length === 0) return;
    setObjects((prev) => [...prev, redoStack[0]]);
    setRedoStack((r) => r.slice(1));
  };
  const deleteLayer = (idx) => setObjects((prev) => prev.filter((_, i) => i !== idx));
  const clearAll = () => { if (objects.length && window.confirm("Clear all annotations?")) { setObjects([]); setRedoStack([]); } };

  const save = async () => {
    setSaving(true);
    try {
      const canvas = canvasRef.current;
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
      const fd = new FormData();
      fd.append("file", blob, `annotated-${(imageName || "image").replace(/\.[^.]+$/, "")}.png`);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onSaved({ url: data.path, name: fd.get("file").name, type: "image", size: blob.size });
      toast.success("Annotated image saved to thread");
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm max-w-4xl" data-testid="annotation-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Pencil size={16} className="text-[#F54800]" /> Annotate — {imageName}</DialogTitle>
        </DialogHeader>

        {/* Toolbar */}
        <div className="flex items-center justify-between gap-3 flex-wrap border-y border-slate-100 py-2">
          <div className="flex items-center gap-1" data-testid="annotation-tools">
            {TOOLS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTool(t.id)}
                title={t.label}
                className={`h-8 w-8 flex items-center justify-center rounded-sm border transition-colors ${tool === t.id ? "bg-[#F54800] border-[#F54800] text-white" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}
                data-testid={`annotation-tool-${t.id}`}
              >
                <t.icon size={15} />
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5" data-testid="annotation-colors">
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                className={`h-6 w-6 rounded-full border-2 ${color === c ? "border-slate-800 scale-110" : "border-white"}`}
                style={{ background: c }}
                data-testid={`annotation-color-${c}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-1">
            <button type="button" onClick={undo} disabled={objects.length === 0} className="h-8 w-8 flex items-center justify-center rounded-sm border border-slate-200 text-slate-600 disabled:opacity-30 hover:bg-slate-50" data-testid="annotation-undo"><Undo2 size={15} /></button>
            <button type="button" onClick={redo} disabled={redoStack.length === 0} className="h-8 w-8 flex items-center justify-center rounded-sm border border-slate-200 text-slate-600 disabled:opacity-30 hover:bg-slate-50" data-testid="annotation-redo"><Redo2 size={15} /></button>
            <button type="button" onClick={clearAll} disabled={objects.length === 0} className="h-8 px-2 flex items-center gap-1 rounded-sm border border-slate-200 text-rose-600 disabled:opacity-30 hover:bg-rose-50 text-xs" data-testid="annotation-clear-all"><Trash2 size={13} /> Clear</button>
          </div>
        </div>

        <div className="flex gap-4 mt-2">
          {/* Canvas */}
          <div className="relative border border-slate-200 rounded-sm overflow-hidden shrink-0" style={{ width: dims.w, height: dims.h }}>
            <canvas
              ref={canvasRef}
              width={dims.w}
              height={dims.h}
              className="cursor-crosshair block"
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
              data-testid="annotation-canvas"
            />
            {textPrompt && (
              <input
                autoFocus
                type="text"
                placeholder="Type feedback…"
                className="absolute text-sm font-semibold px-1.5 py-0.5 border-2 border-[#F54800] rounded-sm outline-none bg-white/95"
                style={{ left: textPrompt.x, top: textPrompt.y - 14, color }}
                onKeyDown={(e) => { if (e.key === "Enter") commitText(e.target.value); if (e.key === "Escape") setTextPrompt(null); }}
                onBlur={(e) => commitText(e.target.value)}
                data-testid="annotation-text-input"
              />
            )}
          </div>

          {/* Layers panel */}
          <div className="flex-1 min-w-[160px]">
            <div className="text-xs font-semibold text-slate-500 inline-flex items-center gap-1.5 mb-2"><Layers size={12} /> Layers ({objects.length})</div>
            <ul className="space-y-1 max-h-[360px] overflow-y-auto pr-1" data-testid="annotation-layers-list">
              {objects.length === 0 && <li className="text-xs text-slate-400 italic">Draw on the image to add a layer.</li>}
              {objects.map((o, idx) => (
                <li key={idx} className="flex items-center justify-between gap-2 text-xs bg-slate-50 rounded-sm px-2 py-1.5" data-testid={`annotation-layer-${idx}`}>
                  <span className="inline-flex items-center gap-1.5 truncate">
                    <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: o.color }} />
                    {layerLabel(o, idx)}
                  </span>
                  <button type="button" onClick={() => deleteLayer(idx)} className="text-slate-400 hover:text-rose-600 shrink-0" data-testid={`annotation-layer-delete-${idx}`}><X size={12} /></button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={onClose} className="rounded-sm" data-testid="annotation-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="annotation-save-share">
            {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Pencil size={14} className="mr-1.5" />} Save &amp; Share
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
